import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../../../shared/core/shared.selectors';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DailyReportService } from '../../../../daily-report.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';
import { AddContractorDialogComponent } from '../add-contractor-dialog/add-contractor-dialog.component';

@Component({
  standalone: false,
  selector: 'app-cle-add-equipment-form',
  templateUrl: './cle-add-equipment-form.component.html',
  styleUrls: ['./cle-add-equipment-form.component.scss'],
})
export class CleAddEquipmentFormComponent implements OnInit, OnDestroy {
  @Input() data: any;

  subContractorOptions = [];
  equipmentTypeOptions = [
    { value: 'vendor_equipment', label: 'Vendor Equipment' },
    { value: 'generic_equipment', label: 'Generic Equipment' },
  ];
  equipmentOptions = [];
  genericEquipmentOptions = [];
  selectedEquipments: any[] = [];
  selectedGenericEquipments: any[] = [];
  expandedEquipments: { [key: string]: boolean } = {};
  expandedGenericEquipments: { [key: string]: boolean } = {};
  equipmentForms: { [key: string]: FormGroup } = {};
  genericEquipmentForms: { [key: string]: FormGroup } = {};
  form = new FormGroup<any>({
    sub_contractor: new FormControl(null, [Validators.required]),
    equipment_type: new FormControl(null, [Validators.required]),
    equipments: new FormControl([]),
    generic_equipments: new FormControl([]),
  });
  errorMessages: any = {};
  errorMessageMap: any = {
    sub_contractor: { required: 'Contractor is required' },
    equipment_type: { required: 'Equipment type is required' },
    equipments: { required: 'At least one equipment is required' },
    generic_equipments: { required: 'At least one generic equipment is required' },
  };
  formSubscription: Subscription = new Subscription();
  private readonly onDestroy: Subject<any> = new Subject<any>();

  private allContractors = [];
  private reportContractors = [];
  private pendingContractorId: string | null = null;

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private commonService: CommonService,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit() {
    if (!this.data) {
      this.dailyReportService.disableOptionMenu.next(true);
    }

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.reportContractors = report?.reportDetails?.sub_contractors || [];
        this.buildContractorOptions();
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.allContractors = project?.sub_contractors || [];
        this.buildContractorOptions();
        this.updateEquipmentOptions();
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        this.genericEquipmentOptions = data?.genericEquipment || [];
      });

    this.form.controls['sub_contractor'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.errorMessages = {};
        this.updateEquipmentOptions();
        this.form.controls['equipment_type'].setValue(null, { emitEvent: false });
        this.form.controls['equipments'].setValue([]);
        this.form.controls['generic_equipments'].setValue([]);
        this.selectedEquipments = [];
        this.selectedGenericEquipments = [];
        this.expandedEquipments = {};
        this.expandedGenericEquipments = {};
        this.equipmentForms = {};
        this.genericEquipmentForms = {};
      });

    this.form.controls['equipment_type'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        this.errorMessages = {};
        this.form.controls['equipments'].setValue([], { emitEvent: false });
        this.form.controls['generic_equipments'].setValue([], { emitEvent: false });
        this.form.controls['equipments'].setErrors(null);
        this.form.controls['generic_equipments'].setErrors(null);
        this.selectedEquipments = [];
        this.selectedGenericEquipments = [];
        this.expandedEquipments = {};
        this.expandedGenericEquipments = {};
        this.equipmentForms = {};
        this.genericEquipmentForms = {};

        if (value === 'vendor_equipment') {
          this.form.controls['equipments'].setValidators([Validators.required]);
          this.form.controls['generic_equipments'].clearValidators();
        } else if (value === 'generic_equipment') {
          this.form.controls['generic_equipments'].setValidators([Validators.required]);
          this.form.controls['equipments'].clearValidators();
        } else {
          this.form.controls['equipments'].clearValidators();
          this.form.controls['generic_equipments'].clearValidators();
        }
        this.form.controls['equipments'].updateValueAndValidity({ emitEvent: false });
        this.form.controls['generic_equipments'].updateValueAndValidity({ emitEvent: false });
      });

    this.form.controls['equipments'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(selectedIds => {
        this.onEquipmentSelectionChange(selectedIds || []);
      });

    this.form.controls['generic_equipments'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(selectedIds => {
        this.onGenericEquipmentSelectionChange(selectedIds || []);
      });

    const initialValues = JSON.stringify(this.form.getRawValue());
    this.formSubscription = this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const currentValues = JSON.stringify(this.form.getRawValue());
        if (currentValues !== initialValues) {
          this.projectsService.isAnyFormDirty.next(true);
          this.formSubscription?.unsubscribe();
        }
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.validate()) {
          this.save();
        }
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.delete();
      });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  validate(): boolean {
    if (this.form.invalid) {
      this.checkForErrors();
      return false;
    }
    return true;
  }

  private buildContractorOptions() {
    this.subContractorOptions = this.reportContractors.map(sc => {
      const projectContractor = this.allContractors.find(c => c.value === sc.sub_contractor?.id);
      return {
        value: sc.sub_contractor?.id,
        label: sc.sub_contractor?.name,
        id: sc.sub_contractor?.id,
        tag: projectContractor?.tag || null,
      };
    }).sort((a, b) => {
      const aPrime = a.tag ? 1 : 0;
      const bPrime = b.tag ? 1 : 0;
      return bPrime - aPrime;
    });

    if (this.pendingContractorId) {
      const found = this.subContractorOptions.find(o => o.value === this.pendingContractorId);
      if (found) {
        this.form.controls['sub_contractor'].setValue(this.pendingContractorId);
        this.pendingContractorId = null;
      }
    }
  }

  private updateEquipmentOptions() {
    const contractorId = this.form.controls['sub_contractor'].value;
    if (contractorId) {
      const contractor = this.allContractors.find(c => c.value === contractorId);
      this.equipmentOptions = contractor?.equipment || [];
    } else {
      this.equipmentOptions = [];
    }
  }

  onEquipmentSelectionChange(selectedIds: any[]) {
    this.selectedEquipments = this.equipmentOptions.filter(o => selectedIds.includes(o.value));
    selectedIds.forEach(id => {
      if (!this.equipmentForms[id]) {
        this.equipmentForms[id] = new FormGroup({
          quantity: new FormControl(null),
          hours: new FormControl(null),
          comment: new FormControl(null),
        });
      }
    });
    Object.keys(this.equipmentForms).forEach(key => {
      if (!selectedIds.includes(key)) {
        delete this.equipmentForms[key];
      }
    });
  }

  onGenericEquipmentSelectionChange(selectedIds: any[]) {
    this.selectedGenericEquipments = this.genericEquipmentOptions.filter(o => selectedIds.includes(o.value));
    selectedIds.forEach(id => {
      if (!this.genericEquipmentForms[id]) {
        this.genericEquipmentForms[id] = new FormGroup({
          quantity: new FormControl(null),
          hours: new FormControl(null),
          comment: new FormControl(null),
        });
      }
    });
    Object.keys(this.genericEquipmentForms).forEach(key => {
      if (!selectedIds.includes(key)) {
        delete this.genericEquipmentForms[key];
      }
    });
  }

  toggleAccordion(equipment: any) {
    const isExpanded = this.expandedEquipments[equipment.value];
    this.expandedEquipments = {};
    if (!isExpanded) {
      this.expandedEquipments[equipment.value] = true;
    }
  }

  toggleGenericEquipmentAccordion(ge: any) {
    const isExpanded = this.expandedGenericEquipments[ge.value];
    this.expandedGenericEquipments = {};
    if (!isExpanded) {
      this.expandedGenericEquipments[ge.value] = true;
    }
  }

  openAddContractorDialog() {
    const dialogRef = this.dialog.open(AddContractorDialogComponent, {
      disableClose: true,
      width: '450px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.sub_contractor_id) {
        const existing = this.subContractorOptions.find(o => o.value === result.sub_contractor_id);
        if (existing) {
          this.form.controls['sub_contractor'].setValue(result.sub_contractor_id);
        } else {
          this.pendingContractorId = result.sub_contractor_id;
        }
      }
    });
  }

  save() {
    const subContractorId = this.form.controls['sub_contractor'].value;
    const equipmentType = this.form.controls['equipment_type'].value;
    const equipmentEntries = [];

    if (equipmentType === 'vendor_equipment') {
      const selectedIds = this.form.controls['equipments'].value || [];
      selectedIds.forEach(id => {
        const eqForm = this.equipmentForms[id]?.getRawValue();
        equipmentEntries.push({
          id: `new_${new Date().getTime()}_${id}`,
          source_type: 'equipment',
          source_id: id,
          sub_contractor_id: subContractorId,
          quantity: eqForm?.quantity || 0,
          duration: eqForm?.hours || 0,
          idle_duration: 0,
          comment: eqForm?.comment || null,
          pictures: [],
          videos: [],
          field_values: [],
        });
      });
    } else if (equipmentType === 'generic_equipment') {
      const selectedIds = this.form.controls['generic_equipments'].value || [];
      selectedIds.forEach(id => {
        const geForm = this.genericEquipmentForms[id]?.getRawValue();
        equipmentEntries.push({
          id: `new_${new Date().getTime()}_${id}`,
          source_type: 'genericEquipment',
          source_id: id,
          sub_contractor_id: subContractorId,
          quantity: geForm?.quantity || 0,
          duration: geForm?.hours || 0,
          idle_duration: 0,
          comment: geForm?.comment || null,
          pictures: [],
          videos: [],
          field_values: [],
        });
      });
    }

    this.dailyReportService.save.next({
      type: 'equipment',
      data: equipmentEntries,
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'equipment',
      id: this.data?.id,
    });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
